import { apiClient, ApiResponse } from './api'

// Project Owner interface
export interface ProjectOwner {
  userId: string
  firstName: string
  lastName: string
  email: string
}

// Project types based on the API response
export interface ProjectApiResponse {
  _id: string
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  deletedAt: string | null
  deletedBy: string | null
  name: string
  description: string
  status: 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'IN_REVIEW'
  tenantId: string
  assetsCount: number
  __v: number
  owners?: ProjectOwner[]
}

export interface ProjectsListResponse {
  success: boolean
  message: string
  data: ProjectApiResponse[]
}

export interface ProjectsPaginatedResponse {
  success: boolean
  message: string
  data: {
    data: ProjectApiResponse[]
    total: number
    page: number
    limit: number
  }
}

class ProjectsApiService {
  private baseEndpoint = '/projects-service/api/v1/projects'

  async getProjects(tenantId?: string | null): Promise<ApiResponse<ProjectApiResponse[]>> {
    try {
      // Build query params for tenant filtering
      const params = new URLSearchParams()
      if (tenantId) {
        params.append('tenantId', tenantId)
      }
      
      const endpoint = params.toString() ? `${this.baseEndpoint}?${params.toString()}` : this.baseEndpoint
      const response = await apiClient.get<ProjectsPaginatedResponse>(endpoint, undefined, 'PROJECTS')
      
      // Transform the paginated response to match the expected format
      if (response.success && response.data) {
        return {
          success: response.success,
          message: response.message,
          data: (response.data as any).data || []
        }
      }
      
      return {
        success: false,
        message: 'Failed to fetch projects',
        data: []
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error)
      throw error
    }
  }

  async getProjectById(projectId: string): Promise<ApiResponse<ProjectApiResponse>> {
    try {
      const response = await apiClient.get<ProjectApiResponse>(`${this.baseEndpoint}/${projectId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to fetch project:', error)
      throw error
    }
  }

  async createProject(projectData: {
    name: string
    description: string
    tenantId: string
    status?: 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'IN_REVIEW'
    owners?: string[]
  }): Promise<ApiResponse<ProjectApiResponse>> {
    try {
      // Transform owners to ownerIds for the API
      const { owners, ...rest } = projectData
      const payload = {
        ...rest,
        ...(owners && owners.length > 0 ? { ownerIds: owners } : {})
      }
      const response = await apiClient.post<ProjectApiResponse>(this.baseEndpoint, payload, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to create project:', error)
      throw error
    }
  }

  async updateProject(projectId: string, projectData: {
    name?: string
    description?: string
    status?: 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'IN_REVIEW'
    owners?: string[]
  }): Promise<ApiResponse<ProjectApiResponse>> {
    try {
      // Transform owners to ownerIds for the API
      const { owners, ...rest } = projectData
      const payload = {
        ...rest,
        ...(owners !== undefined ? { ownerIds: owners } : {})
      }
      const response = await apiClient.patch<ProjectApiResponse>(`${this.baseEndpoint}/${projectId}`, payload, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to update project:', error)
      throw error
    }
  }

  async deleteProject(projectId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`${this.baseEndpoint}/${projectId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to delete project:', error)
      throw error
    }
  }
}

export const projectsApiService = new ProjectsApiService()
export default projectsApiService
